const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
import { dbg } from '../utils/debugLogger';

/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export interface RoomScoreData {
  roomId: string;
  gamesPlayed: number;
  lastActivityAt: string;
  scores: Array<{
    playerId: string;
    playerName: string;
    cumulativeScore: number;
    gamesWon: number;
    gamesPlayed: number;
    winRate: number;
    averageScore: number;
    bestGameScore: number;
    isActive: boolean;
  }>;
}

export interface GameCompletionData {
  gameNumber: number;
  finalScores: {
    biddingTeam: number;
    opposingTeam: number;
  };
  winningTeam: 'bidding' | 'opposing';
  players: Array<{
    userId: string;
    playerName: string;
    team: 'bidding' | 'opposing';
    score: number;
    won: boolean;
  }>;
  totalRounds: number;
  duration?: number;
  gameSettings: {
    pointsToWin: number;
    timeLimit?: number;
    customRules?: Record<string, any>;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class RoomScoreApiService {
  
  /**
   * Get current room scores from server
   */
  async getRoomScores(roomId: string): Promise<RoomScoreData | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rooms/${roomId}/scores`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
    dbg('roomscore', `📊 No room scores found for room ${roomId} (room may be new)`);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<RoomScoreData> = await response.json();
      
      if (!result.success || !result.data) {
        console.warn('⚠️ Room scores API returned unsuccessful response:', result);
        return null;
      }

  dbg('roomscore', '✅ Successfully fetched room scores from server:', result.data);
      return result.data;
      
    } catch (error) {
      console.error('❌ Error fetching room scores from server:', error);
      return null;
    }
  }

  /**
   * Record a completed game on the server
   */
  async recordGameCompletion(roomId: string, gameData: GameCompletionData): Promise<RoomScoreData | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rooms/${roomId}/games`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(gameData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<RoomScoreData> = await response.json();
      
      if (!result.success || !result.data) {
        console.warn('⚠️ Game completion API returned unsuccessful response:', result);
        return null;
      }

  dbg('roomscore', '✅ Successfully recorded game completion on server:', result.data);
      return result.data;
      
    } catch (error) {
      console.error('❌ Error recording game completion on server:', error);
      return null;
    }
  }

  /**
   * Initialize a room on the server
   */
  async initializeRoom(
    roomId: string, 
    createdByUserId: string, 
    gameType: string = 'kadi-ni-tidi',
    maxPlayers: number = 4,
    gameSettings?: any
  ): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rooms/${roomId}/initialize`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          createdByUserId,
          gameType,
          maxPlayers,
          gameSettings: gameSettings || {}
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<any> = await response.json();
      
      if (!result.success) {
        console.warn('⚠️ Room initialization API returned unsuccessful response:', result);
        return false;
      }

  dbg('roomscore', '✅ Successfully initialized room on server:', result.data);
      return true;
      
    } catch (error) {
      console.error('❌ Error initializing room on server:', error);
      return false;
    }
  }

  /**
   * Mark player as active/inactive in room
   */
  async updatePlayerActivity(
    roomId: string, 
    userId: string, 
    isActive: boolean, 
    playerName?: string
  ): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rooms/${roomId}/players/${userId}/active`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          isActive,
          playerName: isActive ? playerName : undefined
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<any> = await response.json();
      
      if (!result.success) {
        console.warn('⚠️ Player activity update API returned unsuccessful response:', result);
        return false;
      }

  dbg('roomscore', `✅ Successfully marked player as ${isActive ? 'active' : 'inactive'} on server`);
      return true;
      
    } catch (error) {
      console.error('❌ Error updating player activity on server:', error);
      return false;
    }
  }

  /**
   * Get room game history
   */
  async getRoomGameHistory(roomId: string, limit: number = 10): Promise<any[] | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rooms/${roomId}/history?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          dbg('roomscore', `📊 No game history found for room ${roomId}`);
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<any[]> = await response.json();
      
      if (!result.success || !result.data) {
        console.warn('⚠️ Game history API returned unsuccessful response:', result);
        return [];
      }

  dbg('roomscore', '✅ Successfully fetched game history from server:', result.data);
      return result.data;
      
    } catch (error) {
      console.error('❌ Error fetching game history from server:', error);
      return [];
    }
  }

  /**
   * Get player global statistics
   */
  async getPlayerGlobalStats(userId: string): Promise<any | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/players/${userId}/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<any> = await response.json();
      
      if (!result.success || !result.data) {
        console.warn('⚠️ Player stats API returned unsuccessful response:', result);
        return null;
      }

      console.log('✅ Successfully fetched player stats from server:', result.data);
      return result.data;
      
    } catch (error) {
      console.error('❌ Error fetching player stats from server:', error);
      return null;
    }
  }
}

export const roomScoreApi = new RoomScoreApiService();